import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Navbar } from '@/components/Navbar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, Key, User } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Login({ setUser }) {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  
  // Forgot Password state
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [passwordResetSent, setPasswordResetSent] = useState(false);
  
  // Forgot Email state
  const [forgotEmailData, setForgotEmailData] = useState({
    company_name: '',
    phone: '',
    first_name: '',
    last_name: ''
  });
  const [forgotEmailLoading, setForgotEmailLoading] = useState(false);
  const [foundEmail, setFoundEmail] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/login`, formData);
      
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      
      setUser(response.data.user);
      toast.success('Login successful!');
      
      // Redirect admin users to admin panel, regular users to dashboard
      if (response.data.user.is_admin) {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!forgotPasswordEmail) {
      toast.error('Please enter your email');
      return;
    }
    
    setForgotPasswordLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/auth/forgot-password`, {
        email: forgotPasswordEmail
      });
      setPasswordResetSent(true);
      toast.success('Password reset link sent to your email');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reset link');
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const handleForgotEmail = async () => {
    if (!forgotEmailData.company_name || !forgotEmailData.phone || 
        !forgotEmailData.first_name || !forgotEmailData.last_name) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setForgotEmailLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/forgot-email`, forgotEmailData);
      setFoundEmail(response.data.email);
      toast.success('Email found!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'No matching account found');
      setFoundEmail('');
    } finally {
      setForgotEmailLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0E1A]">
      <Navbar />
      
      <div className="flex items-center justify-center px-6 py-20" data-testid="login-page">
        <div className="w-full max-w-md">
          <div className="bg-[#0F172A] border border-white/10 rounded-lg p-8">
            <h1 className="text-3xl md:text-4xl font-bold tracking-tighter uppercase mb-2 text-center">
              <span className="text-[#00A6E8]">CLIENT</span>
              <span className="text-white"> LOGIN</span>
            </h1>
            <p className="text-slate-400 text-center mb-8">Access your logistics portal</p>
            
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
              <div>
                <Label htmlFor="email" className="text-slate-300">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="mt-2 bg-[#1E293B] border-white/10 text-white"
                  required
                  data-testid="email-input"
                />
              </div>
              
              <div>
                <Label htmlFor="password" className="text-slate-300">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  className="mt-2 bg-[#1E293B] border-white/10 text-white"
                  required
                  data-testid="password-input"
                />
              </div>
              
              <Button 
                type="submit" 
                disabled={loading}
                className="w-full bg-[#00A6E8] text-[#0A0E1A] font-bold uppercase tracking-wider hover:bg-[#33B8ED] hover:scale-105 transition-all py-6"
                data-testid="login-submit-btn"
              >
                {loading ? 'Logging in...' : 'Login'}
              </Button>
            </form>
            
            {/* Recovery Options */}
            <div className="mt-6 flex flex-col gap-3">
              {/* Forgot Password */}
              <Dialog>
                <DialogTrigger asChild>
                  <button className="text-slate-400 hover:text-[#00A6E8] text-sm flex items-center justify-center gap-2 transition-colors">
                    <Key size={14} />
                    Forgot Password?
                  </button>
                </DialogTrigger>
                <DialogContent className="bg-[#0F172A] border-white/10 text-white">
                  <DialogHeader>
                    <DialogTitle className="text-white flex items-center gap-2">
                      <Key className="text-[#00A6E8]" size={20} />
                      Reset Password
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    {!passwordResetSent ? (
                      <>
                        <p className="text-slate-400 text-sm">
                          Enter your email address and we'll send you a link to reset your password.
                        </p>
                        <div>
                          <Label className="text-slate-300">Email Address</Label>
                          <Input
                            type="email"
                            value={forgotPasswordEmail}
                            onChange={(e) => setForgotPasswordEmail(e.target.value)}
                            className="mt-2 bg-[#1E293B] border-white/10 text-white"
                            placeholder="Enter your email"
                          />
                        </div>
                        <Button 
                          onClick={handleForgotPassword}
                          disabled={forgotPasswordLoading}
                          className="w-full bg-[#00A6E8] text-[#0A0E1A]"
                        >
                          {forgotPasswordLoading ? 'Sending...' : 'Send Reset Link'}
                        </Button>
                      </>
                    ) : (
                      <div className="text-center py-4">
                        <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                          <Mail className="text-green-400" size={32} />
                        </div>
                        <p className="text-green-400 font-semibold mb-2">Reset Link Sent!</p>
                        <p className="text-slate-400 text-sm">
                          Check your email for the password reset link.
                        </p>
                      </div>
                    )}
                  </div>
                </DialogContent>
              </Dialog>
              
              {/* Forgot Email */}
              <Dialog>
                <DialogTrigger asChild>
                  <button className="text-slate-400 hover:text-[#FFA726] text-sm flex items-center justify-center gap-2 transition-colors">
                    <Mail size={14} />
                    Forgot Email?
                  </button>
                </DialogTrigger>
                <DialogContent className="bg-[#0F172A] border-white/10 text-white">
                  <DialogHeader>
                    <DialogTitle className="text-white flex items-center gap-2">
                      <User className="text-[#FFA726]" size={20} />
                      Recover Your Email
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    {!foundEmail ? (
                      <>
                        <p className="text-slate-400 text-sm">
                          Enter your account details and we'll find your registered email.
                        </p>
                        <div>
                          <Label className="text-slate-300">Company Name</Label>
                          <Input
                            value={forgotEmailData.company_name}
                            onChange={(e) => setForgotEmailData({...forgotEmailData, company_name: e.target.value})}
                            className="mt-2 bg-[#1E293B] border-white/10 text-white"
                          />
                        </div>
                        <div>
                          <Label className="text-slate-300">Phone Number</Label>
                          <Input
                            value={forgotEmailData.phone}
                            onChange={(e) => setForgotEmailData({...forgotEmailData, phone: e.target.value})}
                            className="mt-2 bg-[#1E293B] border-white/10 text-white"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label className="text-slate-300">First Name</Label>
                            <Input
                              value={forgotEmailData.first_name}
                              onChange={(e) => setForgotEmailData({...forgotEmailData, first_name: e.target.value})}
                              className="mt-2 bg-[#1E293B] border-white/10 text-white"
                            />
                          </div>
                          <div>
                            <Label className="text-slate-300">Last Name</Label>
                            <Input
                              value={forgotEmailData.last_name}
                              onChange={(e) => setForgotEmailData({...forgotEmailData, last_name: e.target.value})}
                              className="mt-2 bg-[#1E293B] border-white/10 text-white"
                            />
                          </div>
                        </div>
                        <Button 
                          onClick={handleForgotEmail}
                          disabled={forgotEmailLoading}
                          className="w-full bg-[#FFA726] text-[#0A0E1A]"
                        >
                          {forgotEmailLoading ? 'Searching...' : 'Find My Email'}
                        </Button>
                      </>
                    ) : (
                      <div className="text-center py-4">
                        <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                          <Mail className="text-green-400" size={32} />
                        </div>
                        <p className="text-green-400 font-semibold mb-2">Email Found!</p>
                        <p className="text-white text-lg font-mono bg-[#1E293B] px-4 py-2 rounded mt-2">
                          {foundEmail}
                        </p>
                        <p className="text-slate-400 text-sm mt-4">
                          Use this email to login to your account.
                        </p>
                      </div>
                    )}
                  </div>
                </DialogContent>
              </Dialog>
            </div>
            
            <div className="mt-6 text-center border-t border-white/10 pt-6">
              <p className="text-slate-400">
                Don't have an account?{' '}
                <Link to="/register" className="text-[#00A6E8] hover:text-[#33B8ED] font-semibold">
                  Register here
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
